import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart3,
  FileText,
  FolderKanban,
  Files,
  MessageSquare,
  Settings,
  Users,
  Building2,
  ChevronDown,
  LogOut,
  Contact2,
  Kanban,
  Mail,
  FileCode,
  Workflow,
  Plug,
  Share2,
  CheckSquare,
  Star,
  Camera,
  Calculator,
  TrendingDown,
  FlaskConical,
  UserPlus,
  Calendar,
  CalendarDays,
  ClipboardList,
  Grid3X3,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRole } from '@/hooks/portal/useRole';
import { usePortalAuth } from '@/hooks/portal/usePortalAuth';
import logoWhite from '@/assets/logo-white.png';

const mainNavItems = [
  { title: 'Dashboard', url: '/portal', icon: LayoutDashboard },
  { title: 'Calendar', url: '/portal/calendar', icon: CalendarDays },
  { title: 'Reports', url: '/portal/reports', icon: BarChart3 },
  { title: 'Invoices', url: '/portal/invoices', icon: FileText },
  { title: 'Projects', url: '/portal/projects', icon: FolderKanban },
  { title: 'Files', url: '/portal/files', icon: Files },
  { title: 'Support', url: '/portal/support', icon: MessageSquare },
];

const crmNavItems = [
  { title: 'CRM Dashboard', url: '/portal/crm', icon: Contact2 },
  { title: 'Contacts', url: '/portal/crm/contacts', icon: Users },
  { title: 'Companies', url: '/portal/crm/companies', icon: Building2 },
  { title: 'Pipeline', url: '/portal/crm/pipeline', icon: Kanban },
  { title: 'Tasks', url: '/portal/crm/tasks', icon: CheckSquare },
  { title: 'Analytics', url: '/portal/crm/analytics', icon: BarChart3 },
  { title: 'Campaigns', url: '/portal/campaigns', icon: Mail },
  { title: 'Templates', url: '/portal/campaigns/templates', icon: FileCode },
  { title: 'Sequences', url: '/portal/campaigns/sequences', icon: Workflow },
  { title: 'Social Media', url: '/portal/social', icon: Share2 },
];

const adminNavItems = [
  { title: 'Users', url: '/portal/admin/users', icon: Users },
  { title: 'Organizations', url: '/portal/admin/organizations', icon: Building2 },
  { title: 'Provisions', url: '/portal/admin/provisions', icon: UserPlus },
  { title: 'Modules', url: '/portal/admin/modules', icon: Workflow },
  { title: 'Bookings', url: '/portal/admin/bookings', icon: ClipboardList },
  { title: 'Booking Setup', url: '/portal/admin/booking-settings', icon: Calendar },
  { title: 'Funnel Settings', url: '/portal/admin/funnel-settings', icon: Settings },
  { title: 'Intake Forms', url: '/portal/admin/intake-submissions', icon: ClipboardList },
  { title: 'Integrations', url: '/portal/settings/integrations', icon: Plug },
];

const superAdminNavItems = [
  { title: 'Dashboard', url: '/portal/super-admin', icon: LayoutDashboard },
  { title: 'All Companies', url: '/portal/super-admin/companies', icon: Building2 },
  { title: 'All Organizations', url: '/portal/super-admin/organizations', icon: Building2 },
  { title: 'All Users', url: '/portal/super-admin/users', icon: Users },
];

const appsNavItems = [
  { title: 'Reviews Booster', url: '/portal/apps/reviews', icon: Star },
  { title: 'Before & After', url: '/portal/apps/proof', icon: Camera },
  { title: 'Job Pricing', url: '/portal/apps/pricing', icon: Calculator },
  { title: 'Ad Optimizer', url: '/portal/apps/ads', icon: TrendingDown, comingSoon: true },
  { title: 'A/B Tester', url: '/portal/apps/ab-test', icon: FlaskConical, comingSoon: true },
  { title: 'Geo-Grid Tracker', url: '/portal/apps/geo-grid', icon: Grid3X3 },
  { title: 'GBP Scheduler', url: '/portal/apps/gbp-scheduler', icon: CalendarDays },
  { title: 'Dominance Tracker', url: '/portal/apps/dominance-tracker', icon: BarChart3 },
];

export function PortalSidebar() {
  const location = useLocation();
  const { isSuperAdmin, isAdmin, isTeam } = useRole();
  const { profile, signOut } = usePortalAuth();
  const isInternalUser = isAdmin || isTeam;

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border p-2">
        <Link to="/portal" className="flex items-center justify-center">
          <img src={logoWhite} alt="Valentine Digital" className="h-7 group-data-[collapsible=icon]:h-6" />
        </Link>
      </SidebarHeader>

      <SidebarContent className="gap-0">
        <SidebarGroup className="py-1 px-1.5">
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    size="sm"
                    isActive={location.pathname === item.url}
                    tooltip={item.title}
                  >
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span className="text-xs">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isInternalUser && (
          <SidebarGroup className="py-1 px-1.5">
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">CRM</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {crmNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      size="sm"
                      isActive={location.pathname === item.url}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="text-xs">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isInternalUser && (
          <SidebarGroup className="py-1 px-1.5">
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Apps</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {appsNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      size="sm"
                      isActive={location.pathname.startsWith(item.url)}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="text-xs">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isAdmin && (
          <SidebarGroup className="py-1 px-1.5">
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      size="sm"
                      isActive={location.pathname === item.url}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="text-xs">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isSuperAdmin && (
          <SidebarGroup className="py-1 px-1.5">
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider h-6">Super Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                {superAdminNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      size="sm"
                      isActive={location.pathname === item.url}
                      tooltip={item.title}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span className="text-xs">{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-1.5">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="sm" className="w-full">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={profile?.avatar_url ?? undefined} />
                    <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-[10px]">
                      {getInitials(profile?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start text-left min-w-0 flex-1">
                    <span className="text-xs font-medium truncate w-full">
                      {profile?.full_name ?? 'User'}
                    </span>
                  </div>
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/portal/settings" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
